const CART_DRAWER_SCROLL_LOCK_OWNER = 'cart-drawer';

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.renderTransitionTimeout = null;

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    // Delegated: the overlay is re-rendered (Dawn renderContents, gift reconciler), so a
    // listener on the element itself gets lost and taps outside the drawer did nothing.
    this.addEventListener('click', (event) => {
      if (event.target.closest('#CartDrawer-Overlay')) this.close();
    });
    window.addEventListener('chat:drawer:open', () => this.close());
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    window.dispatchEvent(new CustomEvent('cart:drawer:open'));

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    if (window.DrBodyScrollLock) {
      window.DrBodyScrollLock.lock(CART_DRAWER_SCROLL_LOCK_OWNER, 'overflow-hidden');
    } else {
      document.body.classList.add('overflow-hidden');
    }
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    if (window.DrBodyScrollLock) {
      window.DrBodyScrollLock.unlock(CART_DRAWER_SCROLL_LOCK_OWNER);
    } else {
      document.body.classList.remove('overflow-hidden');
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  clearRenderTransition() {
    if (this.renderTransitionTimeout) {
      clearTimeout(this.renderTransitionTimeout);
      this.renderTransitionTimeout = null;
    }
    this.classList.remove('is-rendering');
    this.setAttribute('aria-busy', 'false');
  }

  scheduleRenderTransitionClear() {
    if (this.renderTransitionTimeout) clearTimeout(this.renderTransitionTimeout);
    this.renderTransitionTimeout = setTimeout(() => {
      this.renderTransitionTimeout = null;
      this.classList.remove('is-rendering');
      this.setAttribute('aria-busy', 'false');
    }, 240);
  }

  resolveRenderedCartEmpty(parsedState) {
    const itemCount = Number(parsedState?.item_count);
    if (Number.isFinite(itemCount)) return itemCount === 0;

    const renderedSection = parsedState?.sections?.['cart-drawer'];
    if (typeof renderedSection === 'string' && renderedSection) {
      const renderedDrawer = this.getSectionDOM(renderedSection, '#CartDrawer');
      const renderedItems = renderedDrawer?.querySelector('cart-drawer-items');
      if (renderedItems) return renderedItems.classList.contains('is-empty');
      if (renderedDrawer?.querySelector('.drawer__inner-empty')) return true;
    }

    const liveItems = this.querySelector('cart-drawer-items');
    if (liveItems) return liveItems.classList.contains('is-empty');

    return this.classList.contains('is-empty');
  }

  syncStateFromRenderedContents(parsedState) {
    const nextIsEmpty = this.resolveRenderedCartEmpty(parsedState);
    this.classList.toggle('is-empty', nextIsEmpty);
    if (nextIsEmpty) {
      this.clearRenderTransition();
    }
    return nextIsEmpty;
  }

  renderContents(parsedState) {
    const wasEmpty = this.classList.contains('is-empty');
    const nextIsEmpty = this.syncStateFromRenderedContents(parsedState);
    if (wasEmpty && !nextIsEmpty) {
      this.classList.add('is-rendering');
      this.setAttribute('aria-busy', 'true');
    }
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });
    window.drGiftsReconciler?.afterCartSectionsRendered?.(parsedState, {
      source: 'cart-drawer:render-contents',
      membersMode: 'reset-to-gifts',
    });

    setTimeout(() => {
      this.open();
      if (wasEmpty && !nextIsEmpty) {
        this.scheduleRenderTransitionClear();
      }
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
